import React, { useState, useEffect } from 'react';
import { Card, Button, ButtonGroup, Alert, Spinner, Badge, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faUpload, 
  faFileAlt,
  faExclamationTriangle,
  faClipboardCheck
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import DocumentPlaceholderItem from './DocumentPlaceholderItem';

interface DocumentItem {
  id: string;
  name: string;
  type: string;
  reason?: string;
  timing?: string;
  template_id?: string;
  source?: string;
  priority?: string;
  conditions?: string[];
  transaction_stages?: string[];
}

interface DocumentPlaceholderGroup {
  stage: 'pre_contract' | 'under_contract' | 'post_closing';
  stageLabel: string;
  documents: DocumentItem[];
}

interface DocumentChecklistWithUploadsProps {
  transactionId: string;
  transactionType: string;
  transactionRole?: string;
  transactionStage?: string;
  existingDocuments: any[];
  onDocumentUpload: (document: DocumentItem, file: File, existingDocumentId?: string) => Promise<void>;
  onBulkUpload?: () => void;
  brokerageId?: string;
  refreshTrigger?: number;
}

const getDocumentsByStage = (documents: DocumentItem[]): DocumentPlaceholderGroup[] => {
  // Use transaction_stages array if available (like Smart Checklist)
  // Otherwise fall back to timing-based categorization
  
  const preContract = documents.filter(doc => {
    // First check explicit stage assignment
    if (doc.transaction_stages?.length) {
      return doc.transaction_stages.includes('pre_contract');
    }
    
    // Fall back to timing-based logic for documents without stage assignments
    const timing = doc.timing?.toLowerCase() || '';
    return timing.includes('immediately') || 
           timing.includes('when making offer') ||
           timing.includes('before offer') ||
           timing === 'as specified in contract' ||
           (!timing && !doc.transaction_stages); // Default to pre-contract if no info
  });

  const underContract = documents.filter(doc => {
    // First check explicit stage assignment
    if (doc.transaction_stages?.length) {
      return doc.transaction_stages.includes('under_contract');
    }
    
    // Fall back to timing-based logic
    const timing = doc.timing?.toLowerCase() || '';
    return (timing.includes('within') && timing.includes('days')) ||
           timing.includes('inspection') ||
           timing.includes('contingency') ||
           timing.includes('after acceptance');
  });

  const postClosing = documents.filter(doc => {
    // First check explicit stage assignment
    if (doc.transaction_stages?.length) {
      return doc.transaction_stages.includes('closing') || 
             doc.transaction_stages.includes('post_closing');
    }
    
    // Fall back to timing-based logic
    const timing = doc.timing?.toLowerCase() || '';
    return timing.includes('closing') ||
           timing.includes('settlement') ||
           timing.includes('before closing') ||
           timing.includes('at closing');
  });

  return [
    {
      stage: 'pre_contract',
      stageLabel: '📋 Pre-Contract Documents',
      documents: preContract
    },
    {
      stage: 'under_contract', 
      stageLabel: '📋 Under Contract Documents',
      documents: underContract
    },
    {
      stage: 'post_closing',
      stageLabel: '📋 Post-Closing Documents',
      documents: postClosing
    }
  ];
};

const DocumentChecklistWithUploads: React.FC<DocumentChecklistWithUploadsProps> = ({ 
  transactionId, 
  transactionType, 
  transactionRole,
  transactionStage,
  existingDocuments,
  onDocumentUpload,
  onBulkUpload,
  brokerageId,
  refreshTrigger = 0
}) => {
  const [checklist, setChecklist] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enhancedDocuments, setEnhancedDocuments] = useState<any[]>([]);
  const [transactionDetails, setTransactionDetails] = useState<any>(null);
  
  useEffect(() => {
    console.log('🔄 DocumentChecklistWithUploads mounted/updated:', {
      transactionId,
      transactionType,
      transactionRole,
      transactionStage,
      brokerageId,
      refreshTrigger
    });
    if (transactionId) {
      fetchTransactionDetails();
    }
  }, [transactionId, refreshTrigger]);
  
  useEffect(() => {
    if (transactionDetails && brokerageId) {
      fetchChecklist();
    }
  }, [transactionDetails, brokerageId]);
  
  // Re-fetch checklist when transaction type, role, or stage changes from props
  useEffect(() => {
    if (brokerageId && transactionDetails) {
      console.log('🔄 Transaction properties changed, refreshing checklist...');
      fetchChecklist();
    }
  }, [transactionType, transactionRole, transactionStage]);
  
  useEffect(() => {
    // Enhance existing documents with broker review status
    if (transactionId) {
      enhanceDocumentsWithBrokerStatus();
    }
  }, [transactionId, existingDocuments]); // Fetch when transaction changes or documents update
  
  const fetchTransactionDetails = async () => {
    try {
      console.log('📄 Fetching transaction details for Smart Checklist...');
      const response = await axiosInstance.get(`/transactions/${transactionId}`);
      
      if (response.data.success && response.data.transaction) {
        console.log('✅ [DocumentChecklistWithUploads] Transaction details fetched:', {
          id: response.data.transaction.id,
          type: response.data.transaction.type,
          agent_role: response.data.transaction.agent_role,
          property_data: response.data.transaction.property_data,
          stage: response.data.transaction.stage
        });
        setTransactionDetails(response.data.transaction);
        
        // CRITICAL DEBUG: Check if rental type is being preserved
        if (response.data.transaction.type === 'rental') {
          console.log('🏠 [DocumentChecklistWithUploads] RENTAL TRANSACTION DETECTED - type should be rental in Smart Checklist call');
        }
      } else {
        console.warn('⚠️ No transaction details found');
      }
    } catch (error) {
      console.error('❌ Error fetching transaction details:', error);
      // Don't block the component, continue with props
    }
  };
  
  const enhanceDocumentsWithBrokerStatus = async () => {
    try {
      console.log('🔄 Fetching enhanced documents from broker-review endpoint...');
      console.log('📍 Transaction ID:', transactionId);
      
      // Get auth token from localStorage
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.warn('⚠️ No auth token found, using existing documents');
        setEnhancedDocuments(existingDocuments || []);
        return;
      }
      
      // Fetch documents with broker review status from the dedicated endpoint
      const response = await axiosInstance.get(`/broker-review/documents/${transactionId}`);
      console.log('📡 Broker review response:', response.data);
      
      if (response.data.success && response.data.documents) {
        const docsWithStatus = response.data.documents;
        
        // The broker-review endpoint now includes exemption data, no need for separate fetch
        console.log('📋 Enhanced documents with broker status and exemptions:', {
          count: docsWithStatus.length,
          sample: docsWithStatus[0],
          exemptDocs: docsWithStatus.filter(d => d.status === 'exempt' || d.exemption),
          allDocs: docsWithStatus
        });
        setEnhancedDocuments(docsWithStatus);
      } else {
        console.log('⚠️ No enhanced documents found, using existing documents');
        setEnhancedDocuments(existingDocuments || []);
      }
    } catch (error: any) {
      console.error('❌ Error fetching enhanced documents:', error);
      // Check if it's an auth error
      if (error.response?.status === 401 || error.response?.data?.error === 'Token verification failed') {
        console.error('🔐 Authentication error - token may be invalid');
      }
      setEnhancedDocuments(existingDocuments || []);
    }
  };
  
  const fetchChecklist = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if brokerageId is available
      if (!brokerageId) {
        console.error('❌ No brokerage ID available:', {
          propsBrokerageId: brokerageId,
          transactionBrokerageId: transactionDetails?.brokerage_id,
          transactionBrokerageIdAlt: (transactionDetails as any)?.brokerageId
        });
        setError('Brokerage ID is required to load document checklist');
        return;
      }
      
      console.log('🔍 Making broker-checklist request:', {
        url: `/broker-checklist/${brokerageId}/documents`,
        brokerageId
      });
      
      // Use transaction details if available, otherwise fall back to props
      const txDetails = transactionDetails || {};
      const propertyData = txDetails.property_data || {};
      
      // Build property info from actual transaction data
      const propertyInfo = {
        type: propertyData.property_type || 'residential',
        property_type: 'single_family', // TODO: Add this field to transaction forms
        year_built: propertyData.year_built || null,
        has_hoa: propertyData.has_hoa || false,
        has_pool: propertyData.has_pool || false,
        has_solar: propertyData.has_solar || false,
        solar_ownership: propertyData.solar_ownership || 'owned',
        septic_system: propertyData.septic_system || false,
        water_source: propertyData.water_source || 'city',
        flood_zone: propertyData.flood_zone || '',
        requires_flood_insurance: propertyData.requires_flood_insurance || false
      };
      
      // Build transaction info from actual transaction data
      // Special handling for rental transactions
      const transactionTypeNormalized = (txDetails.type || transactionType || 'purchase').toLowerCase();
      const defaultRole = transactionTypeNormalized === 'rental' ? 'listing_agent' : 'buyer_agent';
      
      const transactionInfo = {
        type: transactionTypeNormalized,
        role: txDetails.agent_role || transactionRole || defaultRole,
        loan_type: propertyData.loan_type || 'conventional',
        current_stage: txDetails.stage || transactionStage || 'pre_contract'
      };
      
      console.log('📤 [DocumentChecklistWithUploads] Sending to broker-checklist API:', {
        brokerageId,
        propertyInfo,
        transactionInfo,
        transactionDetails: txDetails,
        rawTransactionType: txDetails.type,
        normalizedType: (txDetails.type || transactionType || 'purchase').toLowerCase(),
        CRITICAL_TYPE_CHECK: transactionInfo.type === 'rental' ? '🏠 RENTAL DETECTED' : `❌ NOT RENTAL: ${transactionInfo.type}`
      });
      
      // Use the broker-checklist endpoint that actually returns documents
      const response = await axiosInstance.get(`/broker-checklist/${brokerageId}/documents`, {
        params: {
          transaction_type: transactionInfo.type,
          agent_role: transactionInfo.role
        }
      });
      
      if (response.data.success) {
        console.log('📋 Simplified Document API Response:', {
          transaction_type: response.data.transaction_type,
          total_documents: response.data.total_documents,
          matching_documents: response.data.matching_documents,
          sampleDoc: response.data.documents?.[0]
        });
        
        // Helper function to extract transaction stages from AI analysis
        const enhanceDocumentsWithStages = (documents: any[]) => {
          return documents.map(doc => {
            // If document already has transaction_stages, keep it
            if (doc.transaction_stages && doc.transaction_stages.length > 0) {
              return doc;
            }
            
            // Try to extract from ai_analysis (now included in response)
            if (doc.ai_analysis) {
              try {
                const analysis = typeof doc.ai_analysis === 'string' 
                  ? JSON.parse(doc.ai_analysis) 
                  : doc.ai_analysis;
                
                // Extract transaction stages from AI analysis
                const stages = analysis?.usage_context?.when_required?.transaction_stages;
                if (stages && stages.length > 0) {
                  // Normalize stage names to use underscores
                  const normalizedStages = stages.map((stage: string) => 
                    stage.toLowerCase().replace(/[\s-]/g, '_')
                  );
                  console.log(`📋 Extracted stages for ${doc.name}:`, stages, '→', normalizedStages);
                  return {
                    ...doc,
                    transaction_stages: normalizedStages
                  };
                } else {
                  console.log(`⚠️ No stages found in AI analysis for ${doc.name}`, {
                    hasUsageContext: !!analysis?.usage_context,
                    hasWhenRequired: !!analysis?.usage_context?.when_required
                  });
                  
                  // If document is marked as always_required but has no stages,
                  // make it available in all stages
                  if (analysis?.usage_context?.when_required?.always_required) {
                    console.log(`📌 Document ${doc.name} is always required - showing in all stages`);
                    return {
                      ...doc,
                      transaction_stages: ['pre_contract', 'showing', 'under_contract', 'closing']
                    };
                  }
                }
              } catch (e) {
                console.error('Error parsing AI analysis for', doc.name, e);
              }
            }
            
            // No stages found, document will use timing-based logic
            return doc;
          });
        };
        
        // Transform simplified response to checklist format.
        // KOPS-690: split by the endpoint's classification — only primary_category
        // 'required' (isRequired=true) belongs in the Required checklist; everything
        // else (conditional/optional/supplemental) is listed separately and does NOT
        // count toward completion.
        const enhancedDocs = enhanceDocumentsWithStages(response.data.documents || []);
        const isReq = (d: any) => d?.isRequired === true || d?.category === 'required';
        const requiredDocs = enhancedDocs.filter(isReq);
        const conditionalDocs = enhancedDocs.filter((d: any) => !isReq(d));

        const simplifiedChecklist = {
          required: requiredDocs,
          conditional: conditionalDocs,
          recommended: [],
          broker_templates: enhancedDocs
        };
        
        // Debug log to verify stages are extracted
        const settlementDoc = simplifiedChecklist.required.find(d => d.name?.includes('Settlement'));
        if (settlementDoc) {
          console.log('📋 Settlement Sheet stages:', settlementDoc.transaction_stages);
        }
        
        setChecklist(simplifiedChecklist);
      } else {
        setError(response.data.message || 'Failed to load checklist');
      }
    } catch (err: any) {
      console.error('Error fetching checklist:', err);
      setError(err.message || 'Failed to load document checklist');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading document checklist...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        {error}
      </Alert>
    );
  }
  
  // Debug log to see what documents we have
  console.log('📋 Document Checklist Debug:', {
    checklistDocs: checklist?.required?.slice(0, 3).map((d: DocumentItem) => ({
      name: d.name,
      type: d.type,
      id: d.id
    })),
    existingDocsCount: enhancedDocuments.length,
    existingDocs: enhancedDocuments.slice(0, 3).map((d: any) => ({
      name: d.name,
      filename: d.filename || d.original_filename,
      document_type: d.document_type,
      description: d.description,
      id: d.id,
      status: d.status,
      broker_review_status: d.broker_review_status || 'NOT_PRESENT',
      hasEnhancedFields: !!(d.latest_version_type || d.broker_review_status)
    }))
  });
  
  // Log full first document to see all fields
  if (enhancedDocuments.length > 0) {
    console.log('📋 First existing document (all fields):', enhancedDocuments[0]);
  }

  // Combine required and conditional documents
  const allDocuments = [
    ...(checklist?.required || []),
    ...(checklist?.conditional || [])
  ];
  
  const documentsByStage = getDocumentsByStage(allDocuments);
  
  // Enhanced debugging for stage assignment
  console.log('🔍 Pre-Contract Documents:', {
    documents: documentsByStage[0].documents.map(d => ({
      name: d.name,
      transaction_stages: d.transaction_stages,
      timing: d.timing,
      reason: d.transaction_stages?.length ? 'Has stages' : d.timing ? 'Using timing' : 'Default to pre-contract'
    }))
  });
  
  console.log('🔍 Document Stage Grouping:', {
    totalDocs: allDocuments.length,
    preContract: documentsByStage[0].documents.length,
    underContract: documentsByStage[1].documents.length,
    postClosing: documentsByStage[2].documents.length,
    currentStage: transactionStage,
    examplePreContractDoc: documentsByStage[0].documents[0]
  });
  
  // Filter to only show documents for the current stage and earlier stages
  const getRelevantStages = (currentStage: string): string[] => {
    const normalizedStage = currentStage?.toLowerCase().replace(/[\s-]/g, '_');
    
    // Map database stages to document stages
    const stageMapping: Record<string, string[]> = {
      'pre_contract': ['pre_contract'],
      'under_contract': ['pre_contract', 'under_contract'],
      'closing': ['pre_contract', 'under_contract', 'post_closing'],
      'post_closing': ['pre_contract', 'under_contract', 'post_closing'],
      'completed': ['pre_contract', 'under_contract', 'post_closing'],
      'closed': ['pre_contract', 'under_contract', 'post_closing']
    };
    
    return stageMapping[normalizedStage] || ['pre_contract'];
  };
  
  // Get display name for the stage
  const getStageDisplayName = (stage: string): string => {
    const stageNames: Record<string, string> = {
      'pre_contract': 'Pre-Contract',
      'under_contract': 'Under Contract',
      'closing': 'Closing',
      'post_closing': 'Post-Closing',
      'completed': 'Completed'
    };
    
    const normalizedStage = stage?.toLowerCase().replace(/[\s-]/g, '_');
    return stageNames[normalizedStage] || stage;
  };
  
  const relevantStages = getRelevantStages(transactionStage || 'pre_contract');
  const filteredDocumentsByStage = documentsByStage.filter(stageGroup => 
    relevantStages.includes(stageGroup.stage)
  );
  
  // Filter additional documents (not in checklist)
  const additionalDocs = enhancedDocuments.filter(doc => {
    const checklistItems = [...(checklist?.required || []), ...(checklist?.conditional || [])];
    return !checklistItems.some(item => 
      item.name.toLowerCase() === (doc.name || doc.filename || '').toLowerCase() ||
      item.name.toLowerCase() === (doc.description || '').toLowerCase()
    );
  });
  
  // Calculate completion stats using enhanced documents
  const totalRequired = checklist?.required?.length || 0;
  const uploadedRequired = checklist?.required?.filter((doc: DocumentItem) => 
    enhancedDocuments.some(existing => 
      existing.type === doc.type || existing.name === doc.name
    )
  ).length || 0;
  
  return (
    <div>
      {/* Current Stage Indicator */}
      <Alert variant="light" className="mb-3">
        <div>
          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon icon={faClipboardCheck} className="me-2" />
            <strong>Transaction Stage:</strong>
            <Badge bg="primary" className="ms-2">
              {getStageDisplayName(transactionStage || 'pre_contract')}
            </Badge>
          </div>
          <div className="d-flex align-items-center">
            <small className="text-muted">
              Documents shown: 
              {relevantStages.map((stage, index) => (
                <span key={stage}>
                  {index > 0 && ' → '}
                  <strong>{getStageDisplayName(stage)}</strong>
                </span>
              ))}
            </small>
          </div>
        </div>
      </Alert>

      {/* Summary Stats */}
      <Alert variant="info" className="mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong>Document Progress:</strong> {uploadedRequired} of {totalRequired} required documents uploaded
          </div>
          {onBulkUpload && (
            <Button variant="primary" size="sm" onClick={onBulkUpload}>
              <FontAwesomeIcon icon={faUpload} className="me-2" />
              Upload Multiple Documents
            </Button>
          )}
        </div>
      </Alert>
      
      {/* Documents by Stage */}
      {filteredDocumentsByStage.map(stageGroup => {
        const stageDocCount = stageGroup.documents.length;
        const stageUploadedCount = stageGroup.documents.filter(doc =>
          enhancedDocuments.some(existing => 
            existing.type === doc.type || existing.name === doc.name
          )
        ).length;
        
        return (
          <Card key={stageGroup.stage} className="mb-3">
            <Card.Header className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  {stageGroup.stageLabel}
                </h6>
                <span className="badge bg-primary">
                  {stageUploadedCount}/{stageDocCount} Complete
                </span>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {stageGroup.documents.length === 0 ? (
                <div className="text-center text-muted py-3">
                  No documents required for this stage
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {stageGroup.documents.map((doc) => {
                    // Look for an uploaded document that matches this checklist item
                    const uploadedDoc = enhancedDocuments.find(existing => {
                      // First check by document ID (for exemptions without uploads)
                      if (existing.document_id === doc.id || existing.id === doc.id) {
                        console.log(`✅ ID Match found: ${doc.name} = ${existing.id}`, {
                          existingDoc: existing,
                          hasExemption: !!existing.exemption,
                          status: existing.status
                        });
                        return true;
                      }
                      
                      // Handle both API response formats
                      const checklistName = doc.name.toLowerCase();
                      const uploadedName = (existing.name || existing.filename || '').toLowerCase();
                      const uploadedDesc = (existing.description || '').toLowerCase();
                      
                      // Check if name/filename matches checklist name
                      if (uploadedName === checklistName || 
                          uploadedDesc === checklistName) {
                        console.log(`✅ Match found: ${doc.name} = ${existing.name || existing.filename}`);
                        return true;
                      }
                      
                      // For Wire Fraud documents
                      if (checklistName.includes('wire fraud') && 
                          (uploadedDesc.includes('wire fraud') || uploadedName.includes('wire fraud'))) {
                        console.log(`✅ Wire Fraud match: ${doc.name}`);
                        return true;
                      }
                      
                      return false;
                    });
                    
                    if (uploadedDoc) {
                      console.log(`📄 Matched ${doc.name} with uploaded document:`, {
                        filename: uploadedDoc.filename,
                        status: uploadedDoc.status,
                        broker_review_status: uploadedDoc.broker_review_status,
                        exemption: uploadedDoc.exemption,
                        isExempt: uploadedDoc.status === 'exempt' || !!uploadedDoc.exemption
                      });
                    }
                    
                    return (
                      <DocumentPlaceholderItem
                        key={doc.id}
                        document={doc}
                        uploadedDocument={uploadedDoc}
                        transactionId={transactionId}
                        onUpload={async (file) => {
                          await onDocumentUpload(doc, file, uploadedDoc?.id);
                          // Refresh documents after upload to get broker review status
                          setTimeout(() => {
                            enhanceDocumentsWithBrokerStatus();
                          }, 1000);
                        }}
                        onExempt={async (reason) => {
                          if (reason === null) {
                            // Remove exemption
                            try {
                              await axiosInstance.delete(`/transaction-exemptions/transactions/${transactionId}/documents/${doc.id}/exempt`);
                              console.log('✅ Exemption removed for:', doc.name);
                              // Refresh documents
                              enhanceDocumentsWithBrokerStatus();
                            } catch (error) {
                              console.error('❌ Error removing exemption:', error);
                              throw error;
                            }
                          } else {
                            // Add exemption
                            try {
                              await axiosInstance.post(`/transaction-exemptions/transactions/${transactionId}/documents/${doc.id}/exempt`, {
                                reason: reason,
                                exempted_by_role: 'agent'
                              });
                              console.log('✅ Document exempted:', doc.name, 'Reason:', reason);
                              // Refresh documents
                              enhanceDocumentsWithBrokerStatus();
                            } catch (error) {
                              console.error('❌ Error exempting document:', error);
                              throw error;
                            }
                          }
                        }}
                        isRequired={checklist?.required?.some((r: DocumentItem) => r.id === doc.id)}
                      />
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        );
      })}
      
      {/* Additional Documents Section */}
      <Card>
        <Card.Header className="bg-light">
          <h6 className="mb-0">
            📄 Additional Documents
            {additionalDocs.length > 0 && (
              <Badge bg="primary" className="ms-2">{additionalDocs.length}</Badge>
            )}
          </h6>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-2">
            Upload any additional documents not listed above
          </p>
          
          {/* Show uploaded additional documents */}
          {additionalDocs.length > 0 && (
            <ListGroup className="mb-3">
              {additionalDocs.map((doc) => (
                <ListGroup.Item key={doc.id} className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{doc.name || doc.filename}</strong>
                    {doc.broker_review_status && (
                      <Badge 
                        bg={doc.broker_review_status === 'approved' ? 'success' : 
                           doc.broker_review_status === 'rejected' ? 'danger' : 'warning'}
                        className="ms-2"
                      >
                        {doc.broker_review_status}
                      </Badge>
                    )}
                    {doc.compliance_score !== null && doc.compliance_score !== undefined && (
                      <Badge bg="info" className="ms-2">
                        Score: {doc.compliance_score}%
                      </Badge>
                    )}
                  </div>
                  <div>
                    <Button 
                      size="sm" 
                      variant="outline-primary"
                      onClick={() => window.open(doc.url || doc.s3_url, '_blank')}
                    >
                      View
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
          
          <Button variant="outline-primary" size="sm" onClick={onBulkUpload}>
            <FontAwesomeIcon icon={faUpload} className="me-2" />
            Add Additional Documents
          </Button>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DocumentChecklistWithUploads;